/**
 * Date/time helpers for API date (YYYY-MM-DD), time (HH:MM:SS), and timestamps (ISO 8601).
 * Brisbane-only; no timezone shift for calendar dates. Keep in sync with apps/admin/src/utils/dateTime.ts.
 * @see docs/designs/global/date-time-management.md
 */
const BRISBANE_TIMEZONE = 'Australia/Brisbane';

/** Parse YYYY-MM-DD as calendar date (local midnight, no timezone shift). */
export function parseCalendarDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0);
}

/** Format API calendar date for display (e.g. "Wed, 21 Jan 26") in Brisbane context. */
export function formatCalendarDate(dateStr: string): string {
  const date = parseCalendarDate(dateStr);
  return date.toLocaleDateString('en-AU', {
    timeZone: BRISBANE_TIMEZONE,
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  });
}

/** Format TIME string (HH:MM:SS or HH:MM) for display (e.g. "2:30 PM"). */
export function formatTimeOfDay(timeStr: string | null | undefined): string {
  if (!timeStr) return '';
  const parts = timeStr.trim().split(':');
  const hour = parseInt(parts[0], 10);
  const minute = parts[1] ? parseInt(parts[1], 10) : 0;
  if (Number.isNaN(hour)) return '';
  const date = new Date(2000, 0, 1, hour, minute, 0);
  return new Intl.DateTimeFormat('en-AU', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}

/** Format ISO 8601 timestamp as time only in Brisbane (e.g. "2:30 PM"). */
export function formatTimeInBrisbane(isoTimestamp: string | null | undefined): string {
  if (!isoTimestamp) return '';
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: BRISBANE_TIMEZONE,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}

/** Format ISO 8601 timestamp as full date + time in Brisbane. */
export function formatTimestampInBrisbane(isoString: string | null | undefined): string {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: BRISBANE_TIMEZONE,
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}
